import { Calendar } from "@patient-watch/ui/calendar";
import { Input } from "@patient-watch/ui/input";
import { Label } from "@patient-watch/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { Button, cn } from "@patient-watch/ui";
import { selectFieldClearButtonClassName } from "@patient-watch/ui/select";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Calendar as CalendarIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

dayjs.extend(customParseFormat);

interface Props {
    value: Date | null;
    onChange: (date: Date | null) => void;
    label?: string;
    description?: string;
    disabled?: boolean;
    placeholder?: string;
    id?: string;
    className?: string;
    /** Inclusive earliest allowed calendar day. */
    minDate?: Date;
    /** Inclusive latest allowed calendar day. */
    maxDate?: Date;
    /** Shown when the typed date is earlier than minDate. */
    minMessage?: string;
    /** Shown when the typed date is later than maxDate. */
    maxMessage?: string;
    /** Shown when digits form a non-existent calendar date. */
    invalidMessage?: string;
}

const DISPLAY_FORMAT = "DD/MM/YYYY";

function formatDisplay(date: Date | null): string {
    return date ? dayjs(date).format(DISPLAY_FORMAT) : "";
}

/** Keep only digits and insert `/` as DD/MM/YYYY while typing. */
function formatDateMask(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseMaskedDate(text: string): Date | null | undefined {
    const trimmed = text.trim();
    if (!trimmed) return null;

    const digits = trimmed.replace(/\D/g, "");
    if (digits.length > 0 && digits.length < 8) return undefined;

    const candidates = [trimmed, formatDateMask(trimmed)];
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        candidates.push(trimmed);
    }

    for (const candidate of candidates) {
        for (const format of ["DD/MM/YYYY", "D/M/YYYY", "YYYY-MM-DD"] as const) {
            const parsed = dayjs(candidate, format, true);
            if (parsed.isValid()) {
                return parsed.hour(12).minute(0).second(0).millisecond(0).toDate();
            }
        }
    }

    return undefined;
}

function isBeforeMin(date: Date, minDate?: Date): boolean {
    return Boolean(minDate && dayjs(date).isBefore(dayjs(minDate), "day"));
}

function isAfterMax(date: Date, maxDate?: Date): boolean {
    return Boolean(maxDate && dayjs(date).isAfter(dayjs(maxDate), "day"));
}

const DateInput = ({
    value,
    onChange,
    label,
    description,
    disabled,
    placeholder = "DD/MM/YYYY",
    id,
    className,
    minDate,
    maxDate,
    minMessage = "That date is too soon",
    maxMessage = "That date is too far away",
    invalidMessage = "That date doesn't look right",
}: Props) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(() => formatDisplay(value));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setText(formatDisplay(value));
        if (value) setError(null);
    }, [value]);

    const calendarDisabled = useMemo(() => {
        if (disabled) return true;
        return (date: Date) => isBeforeMin(date, minDate) || isAfterMax(date, maxDate);
    }, [disabled, maxDate, minDate]);

    const validateParsed = (parsed: Date | null): string | null => {
        if (!parsed) return null;
        if (isBeforeMin(parsed, minDate)) return minMessage;
        if (isAfterMax(parsed, maxDate)) return maxMessage;
        return null;
    };

    const applyParsed = (parsed: Date | null | undefined, raw: string, options?: { revertOnIncomplete?: boolean }) => {
        if (parsed === undefined) {
            const digits = raw.replace(/\D/g, "");
            if (digits.length === 8) {
                setError(invalidMessage);
                return;
            }
            if (options?.revertOnIncomplete) {
                setText(formatDisplay(value));
                setError(null);
            }
            return;
        }

        const rangeError = validateParsed(parsed);
        if (rangeError) {
            setError(rangeError);
            return;
        }

        setError(null);
        onChange(parsed);
        setText(formatDisplay(parsed));
    };

    const commitText = (raw: string) => {
        applyParsed(parseMaskedDate(raw), raw, { revertOnIncomplete: true });
    };

    const handleTextChange = (raw: string) => {
        const masked = formatDateMask(raw);
        setText(masked);
        setError(null);

        const digits = masked.replace(/\D/g, "");
        if (digits.length === 8) {
            applyParsed(parseMaskedDate(masked), masked);
        } else if (digits.length === 0 && value) {
            setError(null);
            onChange(null);
        }
    };

    return (
        <div className={cn("flex flex-col gap-2 w-full", className)}>
            {label ? (
                <Label htmlFor={id} className="font-medium text-sm">
                    {label}
                </Label>
            ) : null}
            {description ? <p className="text-slate-500 text-sm">{description}</p> : null}
            <div className="flex items-center gap-2">
                <Input
                    id={id}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    data-testid="date-input-text"
                    disabled={disabled}
                    placeholder={placeholder}
                    value={text}
                    aria-invalid={Boolean(error)}
                    onChange={(event) => handleTextChange(event.target.value)}
                    onBlur={() => commitText(text)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            commitText(text);
                            (event.target as HTMLInputElement).blur();
                        }
                    }}
                    className={cn(
                        "bg-white dark:bg-slate-900 h-12",
                        error && "border-red-500 focus-visible:ring-red-300"
                    )}
                />
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            data-testid="date-input-trigger"
                            disabled={disabled}
                            className={selectFieldClearButtonClassName("md")}
                            aria-label="Open calendar"
                        >
                            <CalendarIcon className="opacity-75 w-4 h-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent data-testid="date-input-popover" className="p-0 w-auto" align="end">
                        <Calendar
                            mode="single"
                            selected={value ?? undefined}
                            onSelect={(date) => {
                                if (date) {
                                    const dateWithNoon = dayjs(date).set("hour", 12).set("minute", 0).toDate();
                                    const rangeError = validateParsed(dateWithNoon);
                                    if (rangeError) {
                                        setError(rangeError);
                                        return;
                                    }
                                    setError(null);
                                    onChange(dateWithNoon);
                                    setText(formatDisplay(dateWithNoon));
                                } else {
                                    setError(null);
                                    onChange(null);
                                    setText("");
                                }
                                setOpen(false);
                            }}
                            disabled={calendarDisabled}
                            startMonth={minDate}
                            endMonth={maxDate}
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>
            </div>
            {error ? (
                <p className="text-red-600 text-sm" data-testid="date-input-error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default DateInput;
